import {
  ApiAccountHistoryReportWithNoteData,
  ApiCounterparty,
  ApiTaxClassification,
  useGetByTenantAccountAndAccountSourceAccountIdentifierQuery,
  useGetByTenantCounterpartyAllQuery,
  usePostByTenantNoteChangetaxclassificationAndNoteIdNewTaxClassificationMutation
} from 'api/rtk-api'
import { LabelValueRow } from 'components/core-v2'
import ToastNotification from 'components/core-v2/atoms/toast-notification/toastNotification'
import { ChangeCollateral } from 'components/core-v2/organisms/tables/note-details/change-collateral'
import { format } from 'date-fns'
import React, { useState } from 'react'
import { Currency, formatCurrencyFull } from 'utilities/currency'

import { TENANT } from '../../../../../const'
import { ConfirmationModal } from '../../confirmation-modal'
import { Loading } from './loading'

const Body = ({
  cptyList,
  data
}: {
  cptyList: ApiCounterparty[]
  data: ApiAccountHistoryReportWithNoteData
}) => {
  const [mutateTaxClassification, taxClassificationMutationStatus] =
    usePostByTenantNoteChangetaxclassificationAndNoteIdNewTaxClassificationMutation()

  const { businessUnit, client, note } = data.account

  const [taxClassification, setTaxClassification] = useState(note.taxClassification)
  const [isConfirmingTaxClassChange, setIsConfirmingTaxClassChange] = useState(false)
  const [changeTaxClassSuccess, setChangeTaxClassSuccess] = useState(false)
  const newTaxClassification = taxClassification === 'Capital' ? 'Yield' : 'Capital'
  const confirmationDesc = `Are you sure you want to change the tax classification of this note from ${taxClassification} to ${newTaxClassification}?`
  function changeTaxClassificationAndResetModal(
    newTaxClassification: ApiTaxClassification,
    noteId: string
  ) {
    mutateTaxClassification({
      newTaxClassification,
      noteId,
      tenant: TENANT,
      changeNoteApiTaxClassification: {
        newTaxClassification: 'Yield',
        noteId
      }
    })
    setIsConfirmingTaxClassChange(false)
  }

  if (
    taxClassificationMutationStatus.isSuccess &&
    taxClassificationMutationStatus.originalArgs.newTaxClassification === newTaxClassification
  ) {
    setTaxClassification(newTaxClassification)
    setChangeTaxClassSuccess(true)
  }

  const cptyName = cptyList.find(
    cpty => cpty.counterpartyId === note.counterPartyId
  ).counterpartyName

  const startDate = note.startDate && format(new Date(note.startDate), 'd MMMM yyyy')
  const endDate = note.endDate && format(new Date(note.endDate), 'd MMMM yyyy')

  const { numberActiveTrades, numberClosedTrades, totalValues } = data.noteValueSummary

  return (
    <>
      <LabelValueRow label="Name" value={note.noteName} />
      <LabelValueRow data-private label="Counterparty" value={cptyName} />
      <LabelValueRow data-private label="Client" value={client.clientName} />
      <LabelValueRow data-private label="Business unit" value={businessUnit.businessUnitName} />
      <LabelValueRow label="Note ID (Stock code)" value={note.noteId} />
      <LabelValueRow label="Start date" value={startDate} />
      <LabelValueRow label="End date" value={endDate} />
      <LabelValueRow label="Distribution processing" value={note.dividendProcessing} />
      <LabelValueRow label="Tax classification" value={note.taxClassification} />
      {Object.entries(totalValues).map(([currency, amount]) => (
        <LabelValueRow
          key={currency}
          label="Current value of the note (all trades)"
          value={formatCurrencyFull(amount, currency as Currency)}
        />
      ))}
      <LabelValueRow label="Number of active trades in this note" value={numberActiveTrades} />
      <LabelValueRow label="Number of closed trades in this note" value={numberClosedTrades} />
      <ChangeCollateral noteId={note.noteId} value={note.isCollateralForLoan} />
      {isConfirmingTaxClassChange && (
        <ConfirmationModal
          description={confirmationDesc}
          title="Confirm tax classification change"
          onClose={() => setIsConfirmingTaxClassChange(false)}
          onConfirm={() => changeTaxClassificationAndResetModal(newTaxClassification, note.noteId)}
        />
      )}
      <ToastNotification
        description="Tax classification changed successfully!"
        notificationType="success"
        open={changeTaxClassSuccess}
        title="Tax classification changed"
        onClose={() => setChangeTaxClassSuccess(false)}
      />
    </>
  )
}

interface Props {
  accountId: string
  accountSource: string
}
export const NoteDetailsTable = ({ accountId, accountSource }: Props) => {
  const { data, isLoading: isLoadingAccount } =
    useGetByTenantAccountAndAccountSourceAccountIdentifierQuery({
      tenant: TENANT,
      accountIdentifier: accountId,
      accountSource
    })

  const { data: cptyList, isLoading: isLoadingCptyList } = useGetByTenantCounterpartyAllQuery({
    tenant: TENANT
  })

  const isLoading = isLoadingAccount || isLoadingCptyList

  if (isLoading) return <Loading />

  return <Body cptyList={cptyList} data={data} />
}
